import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { callable } from '../callable';

// ─── Audience targeting ────────────────────────────────────────────────────────

/**
 * Granular audience scope for an announcement.
 * 'seminary' = broadcast to all users in the tenant.
 * 'program'  = all students in a specific program.
 * 'term'     = all students in a specific term/cycle.
 * 'section'  = all students in a specific class/section.
 * 'students' = one or more individually-selected students.
 */
export type AudienceScope = 'seminary' | 'program' | 'term' | 'section' | 'students';

export interface AudienceTarget {
  scope: AudienceScope;
  /** IDs of the targeted entities (programId, termId, sectionId, or personIds). Empty for 'seminary'. */
  ids: string[];
  /** Human-readable label stored for display in the table and student portal. */
  label: string;
}

// ─── Visibility ────────────────────────────────────────────────────────────────

export interface VisibilityConfig {
  /** null means "show immediately" (no future gate). ISO date string means "starts showing on this date". */
  visibleFrom: string | null;
  /** null means "always visible / indefinite". ISO date string means "stops showing after this date". */
  visibleUntil: string | null;
}

// ─── DTOs ──────────────────────────────────────────────────────────────────────

export interface CreateAnnouncementDto {
  tenantId: string;
  title: string;
  /** HTML content from the rich-text editor */
  content: string;
  status: 'draft' | 'published';
  audience: AudienceTarget;
  visibility: VisibilityConfig;
}

export interface UpdateAnnouncementDto {
  tenantId: string;
  announcementId: string;
  title: string;
  content: string;
  status: 'draft' | 'published';
  audience: AudienceTarget;
  visibility: VisibilityConfig;
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const announcementApi = {
  async createAnnouncement(data: CreateAnnouncementDto): Promise<{ id: string }> {
    const { tenantId, ...payload } = data;
    const ref = collection(db, `tenants/${tenantId}/announcements`);

    const docRef = await addDoc(ref, {
      ...payload,
      createdAt: new Date().toISOString(),
      publishedAt: data.status === 'published' ? new Date().toISOString() : null,
    });

    return { id: docRef.id };
  },

  async updateAnnouncement(data: UpdateAnnouncementDto): Promise<void> {
    const { tenantId, announcementId, status, ...payload } = data;
    const ref = doc(db, `tenants/${tenantId}/announcements`, announcementId);

    await updateDoc(ref, {
      ...payload,
      status,
      // Only set publishedAt when transitioning TO published and it wasn't already set
      updatedAt: new Date().toISOString(),
    });
  },
};

// ─── Portal del alumno: SUS anuncios, elegidos por el servidor (S0·SEC-1a) ──

/** Un anuncio como lo recibe el alumno: sin la lista de destinatarios. */
export interface MyAnnouncement {
  id: string;
  title: string;
  content: string;
  audience: { scope: string; label: string };
  createdAt: string | null;
  publishedAt: string | null;
  visibility: VisibilityConfig;
}

export const myAnnouncementsApi = {
  /** El alumno es el de la sesión (o el de «Ver portal»): lo resuelve el servidor. */
  listMine: async (params: { tenantId: string }): Promise<MyAnnouncement[]> => {
    const fn = callable<typeof params, { announcements: MyAnnouncement[] }>('listMyAnnouncements');
    return (await fn(params)).data.announcements;
  },
};
